import { atom, selector, selectorFamily } from 'recoil';
import { euclideanDistanceMm } from '~/domain/geometry/patientGeometry';
import {
    IntensityRegion,
    IntensityRegionDraft,
    IntensityRegionShape,
} from '~/domain/intensity/intensityRegion';
import {
    LineProfile,
    LineProfileDraft,
    MIN_PROFILE_LENGTH_MM,
    ProfileLine,
} from '~/domain/intensity/lineProfile';
import { annotationListState } from '~/state/atoms/annotationListAtoms';
import { KEEP_LATEST_ONLY } from '~/state/atoms/selectorCachePolicy';

/**
 * State of the intensity tool: which shape the next click places, the
 * lines and regions placed so far, and what is selected or pointed at. The
 * line and region types themselves are in `domain/`.
 */

/** The shapes the intensity tool can place: a line, or a region. */
export type IntensityShape = 'line' | IntensityRegionShape;

/**
 * The shape the intensity tool will place on the next click. A line reads
 * the intensity along its length and plots it; a region, a circle or a
 * rectangle, reads the intensity inside it and gives min, max, mean and SD.
 * They are shapes of one tool because all read the same quantity.
 */
export const intensityShapeState = atom<IntensityShape>({
    key: 'intensityShapeState',
    default: 'line',
});

/** The placed line profiles, one atom each, oldest first. */
export const lineProfileListState =
    annotationListState<LineProfile>('lineProfile');

/** The line being placed right now, or null when none is. */
export const lineProfileDraftState = atom<LineProfileDraft | null>({
    key: 'lineProfileDraftState',
    default: null,
});

/**
 * The line being placed, once its end follows the cursor away from its
 * start, so that it has a profile to preview; else null.
 */
export const lineProfilePreviewState = selector<ProfileLine | null>({
    key: 'lineProfilePreviewState',
    get: ({ get }) => {
        const draft = get(lineProfileDraftState);
        return draft?.endMm &&
            euclideanDistanceMm(draft.startMm, draft.endMm) >=
                MIN_PROFILE_LENGTH_MM
            ? { plane: draft.plane, startMm: draft.startMm, endMm: draft.endMm }
            : null;
    },
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});

/** The placed regions, one atom each, oldest first. */
export const intensityRegionListState =
    annotationListState<IntensityRegion>('intensityRegion');

/** The region being placed right now, or null when none is. */
export const intensityRegionDraftState = atom<IntensityRegionDraft | null>({
    key: 'intensityRegionDraftState',
    default: null,
});

/** The selected profile, when the selection is a placed profile; else undefined. */
export const selectedLineProfileState = lineProfileListState.selected;

/** The selected region, when the selection is a placed region; else undefined. */
export const selectedIntensityRegionState = intensityRegionListState.selected;

/** A point on profile `id`, as its distance from the start. */
type ProfileHover = { readonly id: string; readonly distanceMm: number };

/** The point hovered on a profile's chart, as its distance from the start. */
export const profileHoverState = atom<ProfileHover | null>({
    key: 'profileHoverState',
    default: null,
});

/**
 * How far a hovered distance may lie from a sample's or an end's mm and still
 * mark it: the distance comes back from the chart's cm, so it can differ in
 * the last bits.
 */
export const PROFILE_HOVER_TOLERANCE_MM = 1e-6;

/** Clears the hover when it is on profile `id`. */
export const withoutProfileHover =
    (id: string) =>
    (hover: ProfileHover | null): ProfileHover | null =>
        hover?.id === id ? null : hover;

/** The hovered distance on profile `id`, or null; other profiles keep theirs, so they do not redraw. */
export const profileHoverDistanceState = selectorFamily<number | null, string>({
    key: 'profileHoverDistanceState',
    get:
        (id) =>
        ({ get }) => {
            const hover = get(profileHoverState);
            return hover?.id === id ? hover.distanceMm : null;
        },
    cachePolicy_UNSTABLE: KEEP_LATEST_ONLY,
});
